import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@/components/Tooltip';

import { Popover } from './Popover';

export interface LeftNavProps {
  brandLogo: React.JSX.Element;
  mainNavItems: NavItem[];
  footerNavItems?: NavItem[];
  footerTitle?: string;
  collapsed?: boolean;
}

export interface NavItem {
  label: string;
  icon: React.JSX.Element;
  selected?: boolean;
  linkTo?: (children: React.ReactNode) => React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
  items?: SubItem[];
}

interface SubItem {
  label: string;
  icon?: React.JSX.Element;
  linkTo?: (children: React.ReactNode) => React.ReactNode;
  disabled?: boolean;
  tooltip?: string;
  type?: 'header';
}

interface NavItemListProps {
  items: NavItem[];
  collapsed?: boolean;
}

export const LeftNav: React.FC<LeftNavProps> = ({
  brandLogo,
  mainNavItems,
  footerNavItems,
  footerTitle,
  collapsed = false,
}) => {
  // Define the width for collapsed and expanded states
  const collapsedWidth = 100;
  const expandedWidth = 260;

  // Logo size for collapsed/expanded
  const logoSizeCollapsed = 32;
  const logoSizeExpanded = 48;

  // Track if this is the first render
  const [hasMounted, setHasMounted] = React.useState(false);

  // Sliding state - slide entire list up/down
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [translateY, setTranslateY] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState(0);
  const [contentHeight, setContentHeight] = React.useState(0);
  const [isMouseScrolling, setIsMouseScrolling] = React.useState(false);

  const canSlideUp = translateY < 0;
  const canSlideDown =
    contentHeight > containerHeight &&
    Math.abs(translateY) < contentHeight - containerHeight;
  const needsScrolling = contentHeight > containerHeight;

  const slideUp = () => {
    if (canSlideUp) {
      setIsMouseScrolling(false); // Enable transition for button clicks
      setTranslateY(prev => Math.min(0, prev + 150)); // Slide up by 150px
    }
  };

  const slideDown = () => {
    if (canSlideDown) {
      setIsMouseScrolling(false); // Enable transition for button clicks
      const maxTranslate = -(contentHeight - containerHeight);
      setTranslateY(prev => Math.max(maxTranslate, prev - 150)); // Slide down by 150px
    }
  };

  // Handle wheel/trackpad scrolling
  const handleWheel = React.useCallback(
    (e: WheelEvent) => {
      if (!needsScrolling) return;

      e.preventDefault();
      e.stopPropagation();

      setIsMouseScrolling(true); // Disable transition for smooth mouse scrolling

      // Balanced scrolling - responsive but not jumpy
      const scrollAmount = e.deltaY * 0.3; // Sweet spot: responsive but smooth
      const maxTranslate = -(contentHeight - containerHeight);

      setTranslateY(prev => {
        const newTranslateY = prev - scrollAmount;
        // Clamp between 0 and maxTranslate
        return Math.max(maxTranslate, Math.min(0, newTranslateY));
      });
    },
    [needsScrolling, contentHeight, containerHeight]
  );

  // Measure container and content heights
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeights = () => {
      const containerRect = container.getBoundingClientRect();
      const contentElement = container.querySelector('[data-nav-content]');
      if (contentElement) {
        const contentRect = contentElement.getBoundingClientRect();
        setContainerHeight(containerRect.height);
        setContentHeight(contentRect.height);
      }
    };

    // Delay to ensure footer is rendered
    const timer = setTimeout(updateHeights, 100);

    const resizeObserver = new ResizeObserver(updateHeights);
    resizeObserver.observe(container);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [mainNavItems, footerNavItems]);

  // Add wheel event listener for scrolling
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <motion.nav
      className={twMerge(
        'bg-chariot-background-primary flex h-full flex-col p-4'
      )}
      style={{ height: '100vh' }}
      animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
      initial={false}
      transition={{ type: 'tween', duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <motion.div
        className={twMerge(
          collapsed
            ? 'mx-auto mb-8 flex items-center justify-center'
            : 'mt-2 mb-8 ml-4 flex items-center'
        )}
        initial={{
          width: collapsed ? logoSizeCollapsed : logoSizeExpanded,
          height: collapsed ? logoSizeCollapsed : logoSizeExpanded,
          scale: collapsed ? 0.85 : 1,
        }}
        animate={
          hasMounted
            ? {
                width: collapsed ? logoSizeCollapsed : logoSizeExpanded,
                height: collapsed ? logoSizeCollapsed : logoSizeExpanded,
                scale: collapsed ? 0.85 : 1,
              }
            : undefined
        }
        transition={{
          type: 'tween',
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        style={{ overflow: 'hidden' }}
      >
        {brandLogo}
      </motion.div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {/* Top gradient and slide arrow */}
        <motion.div
          className="pointer-events-none absolute top-0 right-0 left-0 z-10"
          animate={{ opacity: canSlideUp ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="from-chariot-background-primary via-chariot-background-primary/90 h-12 bg-linear-to-b to-transparent" />
        </motion.div>

        <motion.button
          className={twMerge(
            'absolute top-2 left-1/2 z-20 -translate-x-1/2 transform rounded-full p-2 transition-all duration-200',
            'hover:bg-chariot-background-secondary text-chariot-text-secondary hover:text-chariot-text-primary',
            canSlideUp ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={slideUp}
          animate={{ opacity: canSlideUp ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronUpIcon className="size-4" />
        </motion.button>

        {/* All navigation content in one scrollable area */}
        <div ref={containerRef} className="flex-1 overflow-hidden">
          <motion.div
            data-nav-content
            className="flex flex-col"
            style={{
              minHeight: needsScrolling ? 'auto' : '100%',
            }}
            animate={{
              y: translateY,
            }}
            transition={
              isMouseScrolling
                ? { duration: 0 }
                : {
                    type: 'tween',
                    duration: 0.3,
                    ease: [0.25, 0.1, 0.25, 1],
                  }
            }
          >
            {/* Main navigation items */}
            <div className={needsScrolling ? '' : 'flex-1'}>
              <NavItemList items={mainNavItems} collapsed={collapsed} />
            </div>

            {/* Footer navigation items */}
            {footerNavItems && footerNavItems.length > 0 && (
              <div className={twMerge('m-0 pt-0')}>
                {footerTitle && !collapsed && (
                  <h3 className="text-chariot-text-secondary mb-2 ml-4 text-sm">
                    {footerTitle}
                  </h3>
                )}
                <NavItemList items={footerNavItems} collapsed={collapsed} />
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom gradient and slide arrow */}
        <motion.div
          className="pointer-events-none absolute right-0 bottom-0 left-0 z-10"
          animate={{ opacity: canSlideDown ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="from-chariot-background-primary via-chariot-background-primary/90 h-12 bg-linear-to-t to-transparent" />
        </motion.div>

        <motion.button
          className={twMerge(
            'absolute bottom-2 left-1/2 z-20 -translate-x-1/2 transform rounded-full p-2 transition-all duration-200',
            'hover:bg-chariot-background-secondary text-chariot-text-secondary hover:text-chariot-text-primary',
            canSlideDown ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
          onClick={slideDown}
          animate={{ opacity: canSlideDown ? 1 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDownIcon className="size-4" />
        </motion.button>
      </div>
    </motion.nav>
  );
};

const NavItemList: React.FC<NavItemListProps> = ({
  items,
  collapsed = false,
}) => {
  return (
    <ul>
      {items.map((item, index) => {
        const navContent = (
          <div
            className={twMerge(
              'text-chariot-brand-primary flex h-full w-full rounded-md py-2 transition-all duration-300 ease-out',
              !collapsed && 'px-5',
              !item.disabled && 'hover:cursor-pointer',
              item.disabled && 'opacity-50',
              !collapsed && 'items-center space-x-6',
              collapsed && 'flex-col items-center space-y-1',
              item.selected &&
                !collapsed &&
                'bg-chariot-brand-tertiary font-semibold',
              item.selected && collapsed && 'font-semibold'
            )}
          >
            <div
              className={twMerge(
                'flex items-center justify-center transition-all duration-300 ease-out',
                collapsed ? 'flex-col' : '',
                item.selected &&
                  collapsed &&
                  'bg-chariot-brand-tertiary h-[27px] w-[54px] rounded-full'
              )}
            >
              <span
                className={twMerge('flex size-6 items-center justify-center')}
              >
                {item.icon}
              </span>
            </div>
            <motion.p
              className={twMerge(
                'text-chariot-text-primary overflow-hidden whitespace-nowrap transition-all duration-300 ease-out',
                !collapsed && 'flex-1',
                collapsed && 'w-full text-center text-[10px]'
              )}
              animate={{
                opacity: collapsed ? 0.9 : 1,
              }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              {item.label}
            </motion.p>
          </div>
        );

        const wrappedContent = item.tooltip ? (
          <Tooltip title={item.tooltip} placement="right">
            {navContent}
          </Tooltip>
        ) : (
          navContent
        );

        const getSubItemContent = (subItem: SubItem) => {
          return (
            <li
              className={twMerge(
                'flex items-center gap-2 px-4',
                subItem.type === 'header' ? 'py-2' : 'py-1',
                subItem.disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-chariot-background-secondary hover:cursor-pointer'
              )}
            >
              {subItem.type === 'header' ? (
                <span className="[&>svg]:size-4">{subItem.icon}</span>
              ) : (
                <span className="size-4"></span>
              )}
              <span
                className={twMerge(
                  subItem.type === 'header'
                    ? 'font-bold'
                    : 'text-chariot-text-secondary text-sm'
                )}
              >
                {subItem.label}
              </span>
            </li>
          );
        };

        const getSubItem = (subItem: SubItem) => {
          return item.tooltip ? (
            <Tooltip title={item.tooltip} placement="right">
              {getSubItemContent(subItem)}
            </Tooltip>
          ) : (
            getSubItemContent(subItem)
          );
        };

        return item.items ? (
          <li className="mb-1 w-full" data-testid="nav-item">
            <Popover
              contentClassName="p-0"
              trigger={wrappedContent}
              position="right"
              showArrow={true}
            >
              <div className="flex min-w-[230px] flex-col pb-2">
                <li className="bg-chariot-background-secondary mb-1 flex items-center gap-2 px-4 py-3 text-lg font-bold">
                  <span className="[&>svg]:size-8">{item.icon}</span>
                  <span>{item.label}</span>
                </li>
                {item.items.map(subItem =>
                  subItem.disabled
                    ? getSubItem(subItem)
                    : subItem.linkTo?.(getSubItem(subItem))
                )}
              </div>
            </Popover>
          </li>
        ) : (
          <li key={index} className="mb-1 w-full" data-testid="nav-item">
            {item.disabled ? wrappedContent : item.linkTo?.(wrappedContent)}
          </li>
        );
      })}
    </ul>
  );
};
